/**
 * CL-401E / CL-403B / CL-405B — Admin property workspace tabs, routes, and navigation helpers.
 */

import { normalizeCmaBuilderStep } from "./cmaBuilderSteps";
import {
  normalizeProfessionalCmaMode,
  PROFESSIONAL_CMA_MODES,
} from "./professionalCmaWorkspaceModel";
import {
  buildPropertyWorkspaceDestinationQuery,
  normalizePropertyWorkspaceDestination,
  PROPERTY_WORKSPACE_DESTINATIONS,
} from "./propertyWorkspaceDestinationModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** CL-RE-HOME-002 — Same six destinations as client workspace. */
export const ADMIN_PROPERTY_WORKSPACE_TABS = PROPERTY_WORKSPACE_DESTINATIONS;

const ADMIN_TAB_IDS = new Set(ADMIN_PROPERTY_WORKSPACE_TABS.map((t) => t.id));

export function normalizeAdminPropertyWorkspaceTab(value) {
  const dest = normalizePropertyWorkspaceDestination(value);
  return ADMIN_TAB_IDS.has(dest) ? dest : "overview";
}

export function buildAdminClientRecordPath(userId, tab = null) {
  const uid = encodeURIComponent(safeStr(userId));
  const base = `/admin/users/${uid}`;
  const normalized = tab ? safeStr(tab).toLowerCase() : "";
  if (normalized === "cmarequests" || normalized === "cma_requests") {
    return `${base}?tab=cmaRequests`;
  }
  if (normalized === "realestate" || normalized === "real_estate") {
    return `${base}?tab=realEstate`;
  }
  return base;
}

export function buildAdminPropertyWorkspacePath(
  userId,
  propertyId,
  tab = "overview",
  {
    section,
    markValuationId,
    requestId,
    cmaStep,
    pcmaMode,
    filesView = null,
    marketPane = null,
    marketSection = null,
  } = {}
) {
  const uid = encodeURIComponent(safeStr(userId));
  const pid = encodeURIComponent(safeStr(propertyId));
  const rawTab = safeStr(tab).toLowerCase();
  const destination = normalizeAdminPropertyWorkspaceTab(tab);
  let resolvedMarketPane = marketPane;
  if (!resolvedMarketPane && (rawTab === "intelligence" || rawTab === "insights")) {
    resolvedMarketPane = "intelligence";
  }
  if (
    !resolvedMarketPane &&
    (rawTab === "professional_cma" || rawTab === "cma_workbench" || rawTab === "workbench")
  ) {
    resolvedMarketPane = "professional_cma";
  }
  let resolvedFilesView = filesView;
  if (!resolvedFilesView && rawTab === "media") resolvedFilesView = "media";
  if (!resolvedFilesView && rawTab === "documents") resolvedFilesView = "documents";

  const params = buildPropertyWorkspaceDestinationQuery(destination, {
    filesView: resolvedFilesView,
    marketPane: resolvedMarketPane,
    marketSection: marketSection || (rawTab === "sale_prep" ? "your_sale" : null),
    section: destination === "market_sale" ? section : null,
    cmaStep,
    markValuationId,
    requestId,
    pcmaMode:
      pcmaMode ||
      (rawTab === "cma_workbench" || rawTab === "workbench" || cmaStep || requestId
        ? PROFESSIONAL_CMA_MODES.BUILDER
        : markValuationId
          ? PROFESSIONAL_CMA_MODES.PUBLISHED
          : null),
    adminMode: true,
  });
  const query = params.toString();
  const base = `/admin/real-estate/client-properties/${uid}/${pid}`;
  return query ? `${base}?${query}` : base;
}

export function buildAdminPropertyWorkspaceQuickLinks(userId, propertyId) {
  const uid = safeStr(userId);
  const pid = safeStr(propertyId);
  if (!uid || !pid) return [];
  return [
    { id: "workspace", label: "Open property workspace", href: buildAdminPropertyWorkspacePath(uid, pid) },
    {
      id: "files_photos",
      label: "Files & photos",
      href: buildAdminPropertyWorkspacePath(uid, pid, "files_photos"),
    },
    {
      id: "market_sale",
      label: "Market & sale",
      href: buildAdminPropertyWorkspacePath(uid, pid, "market_sale", {
        marketPane: "professional_cma",
        pcmaMode: PROFESSIONAL_CMA_MODES.BUILDER,
      }),
    },
  ];
}

/** @deprecated Use buildAdminPropertyWorkspacePath — kept for CL-401D imports. */
export function buildAdminPropertyWorkspacePathLegacy(userId, propertyId, tab = "media") {
  return buildAdminPropertyWorkspacePath(userId, propertyId, tab === "documents" ? "documents" : "media");
}
